/**
 * useEventSource Hook
 * Phase 6: SSE (Server-Sent Events) 연결 및 이벤트 수신
 *
 * WebSocket STOMP를 대체하는 SSE 기반 실시간 알림 수신 훅.
 * Cloud Run 멀티인스턴스 환경에서 안정적으로 동작합니다.
 *
 * 기능:
 * - 서버 heartbeat 감지 (25초 간격) → silent drop 방지
 * - Exponential backoff with jitter → thundering herd 방지
 * - Visibility API → 탭 숨김 시 연결 해제, 복귀 시 재연결
 *
 * 사용법:
 * const { isConnected } = useEventSource('notification', (data) => {
 *   queryClient.invalidateQueries({ queryKey: ['dashboard'] });
 * });
 */

import { useEffect, useRef, useState, useCallback } from "react";
import { useAuthStore } from "@/stores/authStore";
import { getAccessToken } from "@/utils/token";
import apiClient from "@/api/client";

export interface SseMessage {
  type: string;
  organizationId?: number;
  timestamp?: string;
  data?: unknown;
  [key: string]: unknown;
}

interface UseEventSourceOptions {
  /** 연결 활성화 여부 (기본값: true) */
  enabled?: boolean;
  /** 초기 재연결 딜레이 (ms, 기본값: 5000) */
  reconnectDelay?: number;
}

const MAX_RETRY_COUNT = 15;
const HEARTBEAT_TIMEOUT_MS = 90_000; // 90초 (heartbeat 3회분 + 여유)
const HEARTBEAT_CHECK_INTERVAL_MS = 60_000; // 60초마다 체크

/** Exponential backoff with jitter */
function getBackoffDelay(baseDelay: number, attempt: number): number {
  const base = Math.min(baseDelay * Math.pow(2, attempt), 60_000); // max 60s
  const jitter = base * 0.5 * Math.random(); // 0~50% jitter
  return base + jitter;
}

/**
 * SSE 연결 및 이벤트 수신 훅
 *
 * @param eventName SSE 이벤트 이름 (예: 'notification', 'datastore-sync')
 * @param onMessage 메시지 수신 콜백
 * @param options 연결 옵션
 */
export function useEventSource(
  eventName: string,
  onMessage: (message: SseMessage) => void,
  options: UseEventSourceOptions = {},
) {
  const { enabled = true, reconnectDelay = 5000 } = options;
  const [isConnected, setIsConnected] = useState(false);
  const eventSourceRef = useRef<EventSource | null>(null);
  const reconnectTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const heartbeatCheckerRef = useRef<ReturnType<typeof setInterval> | null>(
    null,
  );
  const lastHeartbeatRef = useRef<number>(Date.now());
  const retryCountRef = useRef(0);
  const onMessageRef = useRef(onMessage);
  onMessageRef.current = onMessage;

  const { user } = useAuthStore();
  const organizationId = user?.organization?.id;

  /** 모든 SSE 관련 리소스 정리 */
  const cleanup = useCallback(() => {
    if (eventSourceRef.current) {
      eventSourceRef.current.close();
      eventSourceRef.current = null;
    }
    if (reconnectTimerRef.current) {
      clearTimeout(reconnectTimerRef.current);
      reconnectTimerRef.current = null;
    }
    if (heartbeatCheckerRef.current) {
      clearInterval(heartbeatCheckerRef.current);
      heartbeatCheckerRef.current = null;
    }
  }, []);

  const connect = useCallback(async () => {
    if (!organizationId || !enabled) return;

    // Clean up existing connection
    cleanup();

    const token = getAccessToken();
    if (!token) return;

    // Max retry 초과 시 연결 중단
    if (retryCountRef.current >= MAX_RETRY_COUNT) {
      setIsConnected(false);
      return;
    }

    // 1회용 단기 티켓 발급 (JWT를 URL에 노출하지 않기 위함)
    let ticket: string;
    try {
      const response = await apiClient.post<{ ticket: string }>(
        "/api/sse/ticket",
      );
      ticket = response.data.ticket;
    } catch {
      // 티켓 발급 실패 시 backoff 후 재시도
      if (enabled && retryCountRef.current < MAX_RETRY_COUNT) {
        const delay = getBackoffDelay(reconnectDelay, retryCountRef.current);
        retryCountRef.current += 1;
        reconnectTimerRef.current = setTimeout(() => {
          connect();
        }, delay);
      }
      return;
    }

    // Build SSE URL with short-lived ticket (1회용, URL에 JWT 미노출)
    const baseUrl = import.meta.env.VITE_API_BASE_URL || "";
    const url = `${baseUrl}/api/sse/subscribe/${organizationId}?ticket=${encodeURIComponent(ticket)}`;

    const es = new EventSource(url);
    eventSourceRef.current = es;
    lastHeartbeatRef.current = Date.now();

    es.addEventListener("connected", () => {
      setIsConnected(true);
      retryCountRef.current = 0; // 연결 성공 시 retry count 리셋
    });

    // Heartbeat 감지 (서버 25초 간격 전송)
    es.addEventListener("heartbeat", () => {
      lastHeartbeatRef.current = Date.now();
    });

    es.addEventListener(eventName, (event: MessageEvent) => {
      try {
        const data = JSON.parse(event.data);
        lastHeartbeatRef.current = Date.now(); // 실제 이벤트도 heartbeat로 간주
        onMessageRef.current(data);
      } catch {
        // Failed to parse SSE message
      }
    });

    es.onerror = () => {
      setIsConnected(false);
      cleanup();

      // Exponential backoff 재연결 (재연결 시 티켓도 새로 발급)
      if (enabled && retryCountRef.current < MAX_RETRY_COUNT) {
        const delay = getBackoffDelay(reconnectDelay, retryCountRef.current);
        retryCountRef.current += 1;
        reconnectTimerRef.current = setTimeout(() => {
          connect();
        }, delay);
      }
    };

    // Heartbeat timeout 체크 (silent drop 감지)
    heartbeatCheckerRef.current = setInterval(() => {
      if (Date.now() - lastHeartbeatRef.current > HEARTBEAT_TIMEOUT_MS) {
        // 90초간 heartbeat 미수신 → 연결 죽음, 강제 재연결
        setIsConnected(false);
        cleanup();
        retryCountRef.current = 0; // heartbeat timeout은 새로운 시도
        connect();
      }
    }, HEARTBEAT_CHECK_INTERVAL_MS);
  }, [organizationId, enabled, reconnectDelay, eventName, cleanup]);

  // 메인 연결 effect
  useEffect(() => {
    if (!enabled || !organizationId) {
      cleanup();
      setIsConnected(false);
      return;
    }

    connect();

    return cleanup;
  }, [connect, enabled, organizationId, cleanup]);

  // Visibility API: 탭 숨김 시 연결 해제, 복귀 시 재연결
  useEffect(() => {
    const handleVisibilityChange = () => {
      if (document.hidden) {
        // 탭 숨김 → SSE 연결 해제 (서버 리소스 절약)
        cleanup();
        setIsConnected(false);
      } else if (enabled && organizationId) {
        // 탭 복귀 → 즉시 재연결
        retryCountRef.current = 0;
        connect();
      }
    };

    document.addEventListener("visibilitychange", handleVisibilityChange);
    return () =>
      document.removeEventListener("visibilitychange", handleVisibilityChange);
  }, [connect, cleanup, enabled, organizationId]);

  return { isConnected };
}
